import { Link, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';
import { BRAND } from '@/lib/brand';
import { authClient } from '@/lib/auth-client';
import { cn } from '@/lib/cn';

/**
 * Product logo: the cap-and-book icon plus the name as live text, so the name is never baked into an
 * image. All logo usage in the app goes through this component.
 * Asset: apps/web/public/brand/logo-mark.png (see README there).
 */
export function Wordmark({
  className,
  compact = false,
}: {
  className?: string | undefined;
  /** Smaller height for the mobile top bar. */
  compact?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const { data: session } = authClient.useSession();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Each portal's logo returns to that portal's home.
  const inStaffPortal = pathname === '/admin' || pathname.startsWith('/admin/');
  const homeTo = session ? (inStaffPortal ? '/admin' : '/dashboard') : '/sign-in';

  return (
    <Link
      to={homeTo}
      className={cn('inline-flex items-center gap-2.5 no-underline', className)}
      aria-label={BRAND.product}
    >
      {failed ? (
        <span
          aria-hidden="true"
          className="grid size-9 shrink-0 place-items-center rounded-[10px] bg-primary font-display text-small font-bold text-white"
        >
          EL
        </span>
      ) : (
        <img
          src={BRAND.logo}
          alt=""
          className={cn('w-auto shrink-0 object-contain', compact ? 'h-8' : 'h-10')}
          onError={() => {
            setFailed(true);
          }}
        />
      )}
      <span className="flex flex-col leading-tight">
        <span
          className={cn(
            'font-display font-bold tracking-tight text-primary',
            compact ? 'text-[19px]' : 'text-[22px]',
          )}
        >
          {BRAND.productShort}
        </span>
        <span
          className={cn(
            'wordmark-company font-normal tracking-normal text-muted',
            compact ? 'hidden text-overline min-[380px]:block' : 'text-overline',
          )}
        >
          {BRAND.company}
        </span>
      </span>
    </Link>
  );
}
